import React, { useId, useState } from 'react';
import { Lock } from 'lucide-react';
import { Field, Switch } from '../kit';
import { JOB_TITLE_GROUPS, JOB_TITLES } from './userModel';
import SectionHead from './SectionHead';

const titleChoiceFor = (title) => {
  if (!title) return '';
  return JOB_TITLES.includes(title) && title !== 'Other' ? title : 'Other';
};

/** Profile section of the single-page user panel (invite + edit). */
export default function ProfileSection({ draft, setDraft, errors, isNew, showAdminSwitch, disabled }) {
  const ids = { first: useId(), last: useId(), email: useId(), title: useId(), other: useId(), phone: useId() };
  const [titleChoice, setTitleChoice] = useState(titleChoiceFor(draft.jobTitle));
  const set = (key) => (e) => setDraft((d) => ({ ...d, [key]: e.target.value }));

  const onTitle = (e) => {
    const v = e.target.value;
    setTitleChoice(v);
    setDraft((d) => ({ ...d, jobTitle: v === 'Other' ? (titleChoiceFor(d.jobTitle) === 'Other' ? d.jobTitle : '') : v }));
  };

  return (
    <section className="us-section" aria-labelledby="us-sec-profile">
      <SectionHead id="us-sec-profile" title="Profile" />
      <div className="us-card">
        <div className="ak-grid ak-2">
          <Field label="First name" required htmlFor={ids.first} error={errors.firstName}>
            <input id={ids.first} data-error-key="firstName" className="ak-input" value={draft.firstName} disabled={disabled}
              aria-invalid={errors.firstName ? 'true' : undefined} autoComplete="off" onChange={set('firstName')} />
          </Field>
          <Field label="Last name" required htmlFor={ids.last} error={errors.lastName}>
            <input id={ids.last} data-error-key="lastName" className="ak-input" value={draft.lastName} disabled={disabled}
              aria-invalid={errors.lastName ? 'true' : undefined} autoComplete="off" onChange={set('lastName')} />
          </Field>
        </div>

        <Field
          label="Email"
          required={isNew}
          htmlFor={ids.email}
          error={errors.email}
          help={isNew ? "Used to sign in. It can't be changed after the invite is sent." : "The sign-in email can't be changed. Invite a new user instead."}
        >
          {isNew ? (
            <input id={ids.email} data-error-key="email" type="email" className="ak-input" value={draft.email} disabled={disabled}
              aria-invalid={errors.email ? 'true' : undefined} autoComplete="off" placeholder="name@company.com" onChange={set('email')} />
          ) : (
            <div className="us-input-affix">
              <input id={ids.email} className="ak-input" value={draft.email} readOnly aria-readonly="true" />
              <Lock className="us-affix-icon" aria-hidden="true" />
            </div>
          )}
        </Field>

        <div className="ak-grid ak-2">
          <Field label="Job title" htmlFor={ids.title}>
            <select id={ids.title} className="ak-select" value={titleChoice} disabled={disabled} onChange={onTitle}>
              <option value="">No title</option>
              {JOB_TITLE_GROUPS.map((g) => (
                <optgroup key={g.label} label={g.label}>
                  {g.titles.map((t) => <option key={t} value={t}>{t}</option>)}
                </optgroup>
              ))}
              <option value="Other">Other…</option>
            </select>
          </Field>
          <Field
            label="Mobile phone"
            htmlFor={ids.phone}
            error={errors.phoneNumber}
            help="For text-message MFA. Include the country code, e.g. +15035550100."
          >
            <input id={ids.phone} data-error-key="phoneNumber" type="tel" className="ak-input" value={draft.phoneNumber} disabled={disabled}
              aria-invalid={errors.phoneNumber ? 'true' : undefined} placeholder="+15035550100" autoComplete="off" onChange={set('phoneNumber')} />
          </Field>
        </div>

        {titleChoice === 'Other' ? (
          <Field label="Other title" htmlFor={ids.other}>
            <input id={ids.other} className="ak-input" value={draft.jobTitle} disabled={disabled} placeholder="e.g. Revenue Analyst" onChange={set('jobTitle')} />
          </Field>
        ) : null}

        {showAdminSwitch ? (
          <div className="us-switch-row ak-divider">
            <Switch
              title="Lodgeic administrator"
              checked={Boolean(draft.isAdmin)}
              disabled={disabled}
              onChange={(v) => setDraft((d) => ({ ...d, isAdmin: v ? 1 : 0 }))}
            />
            <div>
              <span className="us-switch-title">Lodgeic administrator</span>
              <span className="ak-help">Full access to every organization and the Admin pages. Only Lodgeic staff see this switch.</span>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}
